import { Dispatch, useEffect, useMemo, useState } from 'react'
import { useOurStore } from '@/app/_providers/StoreProvider'
import AdditionalInfoResponse = DataAccessInput.AdditionalInfoResponse
import AdditionalInfoVisibleStatus = DataAccessInput.AdditionalInfoVisibleStatus
import TicketCategory = Transformed.TicketCategory
import LineItem = Transformed.LineItem

type ReturnType = {
  visibleStatus: AdditionalInfoVisibleStatus
  additionalInfoResponses: AdditionalInfoResponse[]
  setAdditionalInfoResponses: Dispatch<AdditionalInfoResponse[]>
  handleUpdateVisibleStatus: (updatedLocalLineItems: LineItem[], category: TicketCategory) => void
}

/**
 * Create initial additional info responses from category.additionalInfo
 * and merge with any incoming additional info responses
 * @param {TicketCategory[]} categories
 * @param {AdditionalInfoResponse[]} incomingAdditionalInfoResponses
 * @returns {AdditionalInfoResponse[]}
 */
export const createInitialAdditionalInfoResponses = (
  categories: TicketCategory[] | readonly TicketCategory[],
  incomingAdditionalInfoResponses: AdditionalInfoResponse[],
): AdditionalInfoResponse[] => (
  categories.flatMap((category) => (category.additionalInfo || [])
    .filter((info): info is NonNullable<typeof info> => !!info)
    .map((info) => {
      const incoming = incomingAdditionalInfoResponses.find(
        (response) => response.categoryId === category.id && response.id === info.id,
      )

      let initialValue: string | boolean | number = info.type === 'Boolean' ? false : ''

      if (incoming) {
        initialValue = incoming.value
      }

      return {
        categoryId: category.id,
        value: initialValue,
        required: info.required,
        id: info.id,
        key: info.key,
        type: info.type,
      }
    })))

/**
 * Update visible state
 * @param {AdditionalInfoVisibleStatus} prev - previous state
 * @param {LineItem[]} updatedLocalLineItems - local line items
 * @param {TicketCategory} category  - ticket category
 * @returns {AdditionalInfoVisibleStatus}
 */
export const updateVisibleState = (
  prev: AdditionalInfoVisibleStatus,
  updatedLocalLineItems: LineItem[],
  category: TicketCategory,
): AdditionalInfoVisibleStatus => {
  const matchedLineItems = updatedLocalLineItems.filter((listItem) => listItem.category.id === category.id)
  let status = false

  if (matchedLineItems.length) {
    // only set to true if there is additional info
    status = !!category.additionalInfo
  }

  return {
    ...prev,
    [category.id]: status,
  }
}

type Props = {
  categories: TicketCategory[] | readonly TicketCategory[] | undefined
}

const useAdditionalInfo = ({ categories }: Props): ReturnType => {
  const localLineItems = useOurStore((state) => state.localLineItems)
  const updateAdditionalInfoResponses = useOurStore((state) => state.updateAdditionalInfoResponses)
  const additionalInfoResponses = useOurStore((state) => state.additionalInfoResponses)
  const [visibleStatus, setVisibleStatus] = useState<AdditionalInfoVisibleStatus>({})

  // We will never want to handle sold-out categories
  const filteredCategories = useMemo(() => (
    categories?.filter((category) => category && !category.soldOut) || []
  ), [categories])

  const setAdditionalInfoResponses = (incomingValues: DataAccessInput.AdditionalInfoResponse[]): void => {
    updateAdditionalInfoResponses(incomingValues)
  }

  const mergedResponses = useMemo(() => (
    createInitialAdditionalInfoResponses(filteredCategories, additionalInfoResponses)
  ), [filteredCategories, additionalInfoResponses])

  const handleUpdateVisibleStatus = (
    updatedLocalLineItems: LineItem[],
    category: TicketCategory,
  ): void => {
    // update visible status state
    setVisibleStatus((prev) => updateVisibleState(prev, updatedLocalLineItems, category))
  }

  // set visible status
  useEffect(() => {
    const nextVisibleStatus = (localLineItems.length && filteredCategories.length)
      ? filteredCategories.reduce((acc, category) => (
        updateVisibleState(acc, localLineItems, category)
      ), {} as AdditionalInfoVisibleStatus)
      : {}

    // Only update if it actually changed to avoid unnecessary re-renders or infinite loops
    setVisibleStatus((prev) => (
      JSON.stringify(prev) === JSON.stringify(nextVisibleStatus) ? prev : nextVisibleStatus
    ))
  }, [localLineItems, filteredCategories])

  // we only want this to fire when filteredCategories and visibleStatus change
  useEffect(() => {
    // persist the merged values for the cart payload
    if (Object.keys(visibleStatus).length && filteredCategories) {
      updateAdditionalInfoResponses(mergedResponses)
    }
  }, [filteredCategories, visibleStatus])

  return {
    visibleStatus,
    additionalInfoResponses: mergedResponses,
    setAdditionalInfoResponses,
    handleUpdateVisibleStatus,
  }
}

export default useAdditionalInfo
